// src/services/event.js
import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";
import { formatPacific } from "../utils/pacificTime";

const eventApi = createApi({
  reducerPath: "event",
  baseQuery: prepareBaseQuery("/events/"),
  tagTypes: ["Event"],
  endpoints: (builder) => ({
    getAllEvents: builder.query({
      query: (params = {}) => ({
        url: "get-all",
        params,
      }),
      providesTags: ["Event"],
      transformResponse: (response) => {
        return response.map((event) => {
          // If event has new format, use it
          if (event.startDateTime && event.endDateTime) {
            return {
              ...event,
              date: event.startDateTime,
              startTime: formatPacific(event.startDateTime, "HH:mm"),
              endTime: formatPacific(event.endDateTime, "HH:mm"),
            };
          }
          // Otherwise, use old format
          return event;
        });
      },
    }),
    getEventById: builder.query({
      query: (eventId) => ({ url: `by-id/${eventId}`, method: "GET" }),
      // The specific tag lets the portal admin's apply refetch this one
      // event without refetching the list (services/portalAdmin.js).
      providesTags: (r, e, id) => [{ type: "Event", id }, "Event"],
      transformResponse: (response) => {
        const event = response?.event;
        if (!event) return null;
        // Match getAllEvents row normalization (date/startTime/endTime) so the
        // admin modal renders identically whether the event came from the list
        // or from this by-id fetch.
        if (event.startDateTime && event.endDateTime) {
          return {
            ...event,
            date: event.startDateTime,
            startTime: formatPacific(event.startDateTime, "HH:mm"),
            endTime: formatPacific(event.endDateTime, "HH:mm"),
          };
        }
        return event;
      },
    }),
    getUnsettledEvents: builder.query({
      query: () => ({ url: "unsettled", method: "GET" }),
      providesTags: ["Event"],
      transformResponse: (response) => {
        const items = Array.isArray(response?.items) ? response.items : [];
        return items.map((event) => {
          // If event has new format, use it
          if (event.startDateTime && event.endDateTime) {
            return {
              ...event,
              date: event.startDateTime,
              startTime: formatPacific(event.startDateTime, "HH:mm"),
              endTime: formatPacific(event.endDateTime, "HH:mm"),
            };
          }
          // Otherwise, use old format
          return event;
        });
      },
    }),
    getAllConfEvents: builder.query({
      query: () => "get-all-conf",
      providesTags: ["Event"],
      transformResponse: (response) => {
        return response.map((event) => {
          // If event has new format, use it
          if (event.startDateTime && event.endDateTime) {
            return {
              ...event,
              date: event.startDateTime,
              startTime: formatPacific(event.startDateTime, "HH:mm"),
              endTime: formatPacific(event.endDateTime, "HH:mm"),
            };
          }
          // Otherwise, use old format
          return event;
        });
      },
    }),
    addEvent: builder.mutation({
      query: (formData) => {
        // The formData already contains startDateTime and endDateTime as ISO strings
        // We don't need to do any conversion here
        return {
          url: "create-one",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Event"],
    }),
    updateEvent: builder.mutation({
      query: ({ eventId, data }) => {
        // If data is FormData, we don't need to do any conversion
        // The component has already handled the datetime conversion
        return {
          url: `update-one/${eventId}`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: ["Event"],
    }),
    deleteEvent: builder.mutation({
      query: (id) => ({
        url: `delete-one/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Event"],
    }),
    uploadInvoice: builder.mutation({
      query: ({ eventId, formData }) => ({
        url: `upload-invoice/${eventId}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),
    uploadDeposit: builder.mutation({
      query: ({ eventId, formData }) => ({
        url: `upload-deposit/${eventId}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),
    getInvoice: builder.query({
      query: (eventId) => ({
        url: `invoice/${eventId}`,
        method: "GET",
        responseHandler: (response) => (response.ok ? response.blob() : response.json()),
      }),
      skip: (eventId) => !eventId,
      providesTags: ["Event"],
    }),
    getDeposit: builder.query({
      query: (eventId) => ({
        url: `deposit/${eventId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      skip: (eventId) => !eventId,
      providesTags: ["Event"],
    }),
    confirmEvent: builder.mutation({
      query: ({
        eventId,
        sendEmail,
        attachDepositReceipt,
        depositPaid,
        toEmails,
      }) => ({
        url: `confirm-event`,
        method: "POST",
        body: {
          eventId,
          sendEmail,
          ...(attachDepositReceipt !== undefined
            ? { attachDepositReceipt }
            : {}),
          ...(depositPaid !== undefined ? { depositPaid } : {}),
          ...(Array.isArray(toEmails) && toEmails.length ? { toEmails } : {}),
        },
      }),
      invalidatesTags: ["Event"],
    }),
    unconfirmEvent: builder.mutation({
      query: (eventId) => ({
        url: `unconfirm-event`,
        method: "POST",
        body: eventId,
      }),
      invalidatesTags: ["Event"],
    }),
    // Payments and extras (djr-be controllers/eventMoneyController.js).
    addEventPayment: builder.mutation({
      query: ({ eventId, ...body }) => ({ url: `${eventId}/payments`, method: "POST", body }),
      invalidatesTags: ["Event"],
    }),
    updateEventPayment: builder.mutation({
      query: ({ eventId, paymentId, ...body }) => ({ url: `${eventId}/payments/${paymentId}`, method: "PATCH", body }),
      invalidatesTags: ["Event"],
    }),
    removeEventPayment: builder.mutation({
      query: ({ eventId, paymentId }) => ({ url: `${eventId}/payments/${paymentId}`, method: "DELETE" }),
      invalidatesTags: ["Event"],
    }),
    addEventExtra: builder.mutation({
      query: ({ eventId, ...body }) => ({ url: `${eventId}/extras`, method: "POST", body }),
      invalidatesTags: ["Event"],
    }),
    removeEventExtra: builder.mutation({
      query: ({ eventId, extraId }) => ({ url: `${eventId}/extras/${extraId}`, method: "DELETE" }),
      invalidatesTags: ["Event"],
    }),
    finalPaymentEvent: builder.mutation({
      query: ({ eventId, sendEmail, includeReceiptAttachment, toEmails }) => ({
        url: `final-payment-event`,
        method: "POST",
        body: {
          eventId,
          sendEmail,
          includeReceiptAttachment,
          ...(Array.isArray(toEmails) && toEmails.length ? { toEmails } : {}),
        },
      }),
      invalidatesTags: ["Event"],
    }),
    togglePublic: builder.mutation({
      query: (eventId) => ({
        url: `toggle-public-event`,
        method: "POST",
        body: { eventId },
      }),
      invalidatesTags: ["Event"],
    }),
    toggleCancelled: builder.mutation({
      query: (eventId) => ({
        url: `toggle-cancelled-event`,
        method: "POST",
        body: { eventId },
      }),
      invalidatesTags: ["Event"],
    }),
    uploadFinal: builder.mutation({
      query: ({ eventId, formData }) => ({
        url: `upload-final/${eventId}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),
    getFinal: builder.query({
      query: (eventId) => ({
        url: `final/${eventId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      skip: (eventId) => !eventId,
      providesTags: ["Event"],
    }),
    // Generic typed attachments (contract, notes PDF, rider…).
    // See src/config/attachmentTypes.js.
    getEventAttachments: builder.query({
      query: (eventId) => `${eventId}/attachments`,
      providesTags: ["Event"],
    }),
    uploadEventAttachment: builder.mutation({
      query: ({ eventId, formData }) => ({
        url: `${eventId}/attachments`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),
    deleteEventAttachment: builder.mutation({
      query: ({ eventId, attachmentId }) => ({
        url: `${eventId}/attachments/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Event"],
    }),
    getEventAttachmentFile: builder.query({
      query: ({ eventId, attachmentId }) => ({
        url: `${eventId}/attachments/${attachmentId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      providesTags: ["Event"],
    }),
    // Server-rendered PDFs (admin v2). GET previews without saving; POST
    // renders and saves a new event attachment (older versions are kept).
    getServerDocument: builder.query({
      query: ({ eventId, type }) => ({
        url: `${eventId}/documents/${type}`,
        method: "GET",
        // A refusal (409 FINAL_INVOICE_PAID and the like) is JSON with a msg.
        responseHandler: (response) => (response.ok ? response.blob() : response.json()),
      }),
      keepUnusedDataFor: 0,
    }),
    // Same GET for the run sheet PDFs (runsheet, client-timeline), with the
    // facts the server sends as exposed headers: X-Document-Internal and,
    // for client-timeline, X-Dropped-For-Money and X-Dropped-For-Contact.
    // Answers { blob, internal, droppedForMoney, droppedForContact }.
    getServerDocumentWithFacts: builder.query({
      query: ({ eventId, type }) => ({
        url: `${eventId}/documents/${type}`,
        method: "GET",
        // A refusal (409 FINAL_INVOICE_PAID and the like) is JSON with a msg.
        responseHandler: (response) => (response.ok ? response.blob() : response.json()),
      }),
      transformResponse: (blob, meta) => {
        const headers = meta?.response?.headers;
        const read = (name) => (typeof headers?.get === "function" ? headers.get(name) : null);
        const count = (name) => {
          const n = Number(read(name) ?? 0);
          return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
        };
        return {
          blob: blob ?? null,
          internal: String(read("X-Document-Internal") ?? "").toLowerCase() === "true",
          droppedForMoney: count("X-Dropped-For-Money"),
          droppedForContact: count("X-Dropped-For-Contact"),
        };
      },
      keepUnusedDataFor: 0,
    }),
    saveServerDocument: builder.mutation({
      query: ({ eventId, type }) => ({
        url: `${eventId}/documents/${type}`,
        method: "POST",
      }),
      invalidatesTags: ["Event"],
    }),
    // Whitelisted fields of event.contract (template, specialTerms,
    // balanceMethod, serviceLines, lightingLabel, notes).
    updateEventContract: builder.mutation({
      query: ({ eventId, contract }) => ({
        url: `${eventId}/contract`,
        method: "PATCH",
        body: { contract: contract ?? {} },
      }),
      invalidatesTags: (r, e, arg) => [
        { type: "Event", id: arg?.eventId },
        "Event",
      ],
    }),
    uploadReceipt: builder.mutation({
      query: ({ eventId, formData }) => ({
        url: `upload-receipt/${eventId}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),
    getReceipt: builder.query({
      query: (eventId) => ({
        url: `receipt/${eventId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      skip: (eventId) => !eventId,
      providesTags: ["Event"],
    }),
    sendFinalBillEmail: builder.mutation({
      query: ({ eventId, customNote, toEmails }) => ({
        url: "send-final-bill",
        method: "POST",
        body: {
          eventId,
          customNote,
          ...(Array.isArray(toEmails) && toEmails.length ? { toEmails } : {}),
        },
      }),
    }),
    getEventImageUrl: builder.query({
      query: (eventId) => `/${eventId}/image-url`,
      providesTags: ["Event"],
    }),
    uploadDepositReceipt: builder.mutation({
      query: ({ eventId, formData }) => ({
        url: `upload-deposit-receipt/${eventId}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Event"],
    }),
    getDepositReceipt: builder.query({
      query: (eventId) => ({
        url: `deposit-receipt/${eventId}`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
      skip: (eventId) => !eventId,
      providesTags: ["Event"],
    }),
    checkAvailability: builder.mutation({
      query: ({ startDateTime, endDateTime }) => ({
        url: `check-availability`,
        method: "POST",
        body: { startDateTime, endDateTime },
      }),
    }),
    getEventEmails: builder.query({
      query: (eventId) => `${eventId}/emails`,
      providesTags: ["Event"],
    }),
    refreshEventEmailLinks: builder.mutation({
      query: (eventId) => ({
        url: `${eventId}/emails/refresh-links`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Event"],
    }),
    linkThreadToEvent: builder.mutation({
      query: ({ eventId, threadId }) => ({
        url: `${eventId}/emails/link-thread`,
        method: "POST",
        body: { threadId },
      }),
      invalidatesTags: ["Event"],
    }),
    unlinkThreadFromEvent: builder.mutation({
      query: ({ eventId, threadId }) => ({
        url: `${eventId}/emails/unlink-thread`,
        method: "POST",
        body: { threadId },
      }),
      invalidatesTags: ["Event"],
    }),
    linkThreadsToEvent: builder.mutation({
      query: ({ eventId, threadIds }) => ({
        url: `${eventId}/emails/link-threads`,
        method: "POST",
        body: { threadIds },
      }),
      invalidatesTags: ["Event"],
    }),
    createEventDraftFromEmail: builder.mutation({
      query: ({ threadId, threadIds }) => ({
        url: "from-email",
        method: "POST",
        body:
          Array.isArray(threadIds) && threadIds.length
            ? { threadIds }
            : { threadId },
      }),
    }),
    createEventDraftFromText: builder.mutation({
      query: ({ text }) => ({
        url: "from-text",
        method: "POST",
        body: { text },
      }),
    }),
    // Admin v2 Phase 2 (2026-09-29).
    // GET /events/admin/pricing (JWT): the full djr-be pricing-config
    // (baseByDuration, eventTypePremium, modifiers, addOns with prices,
    // deposit { rate, roundUpTo }, travelZones). The form falls back to its
    // legacy numbers while this has not loaded or fails.
    getAdminPricing: builder.query({
      query: () => ({ url: "admin/pricing", method: "GET" }),
      keepUnusedDataFor: 600,
    }),
    // PATCH /events/:eventId/needs (JWT): { needs: { mics, extraSpeakers,
    // subwoofers } | null, rentalLines: [{ label, qty, supplier, note }] }.
    patchEventNeeds: builder.mutation({
      query: ({ eventId, needs, rentalLines }) => ({
        url: `${eventId}/needs`,
        method: "PATCH",
        body: { needs: needs ?? null, rentalLines: rentalLines ?? [] },
      }),
      invalidatesTags: ["Event"],
    }),
  }),
});

export const {
  useGetEventAttachmentsQuery,
  useUploadEventAttachmentMutation,
  useDeleteEventAttachmentMutation,
  useLazyGetEventAttachmentFileQuery,
  useLazyGetServerDocumentQuery,
  useLazyGetServerDocumentWithFactsQuery,
  useSaveServerDocumentMutation,
  useUpdateEventContractMutation,
  useGetAllEventsQuery,
  useGetEventByIdQuery,
  useLazyGetEventByIdQuery,
  useGetUnsettledEventsQuery,
  useGetAllConfEventsQuery,
  useAddEventMutation,
  useAddEventPaymentMutation,
  useUpdateEventPaymentMutation,
  useRemoveEventPaymentMutation,
  useAddEventExtraMutation,
  useRemoveEventExtraMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useUploadInvoiceMutation,
  useUploadDepositMutation,
  useGetInvoiceQuery,
  useGetDepositQuery,
  useLazyGetInvoiceQuery,
  useLazyGetDepositQuery,
  useConfirmEventMutation,
  useUnconfirmEventMutation,
  useFinalPaymentEventMutation,
  useTogglePublicMutation,
  useToggleCancelledMutation,
  useUploadFinalMutation,
  useGetFinalQuery,
  useLazyGetFinalQuery,
  useUploadReceiptMutation,
  useGetReceiptQuery,
  useLazyGetReceiptQuery,
  useSendFinalBillEmailMutation,
  useGetEventImageUrlQuery,
  useUploadDepositReceiptMutation,
  useGetDepositReceiptQuery,
  useLazyGetDepositReceiptQuery,
  useCheckAvailabilityMutation,
  useGetEventEmailsQuery,
  useRefreshEventEmailLinksMutation,
  useLinkThreadToEventMutation,
  useUnlinkThreadFromEventMutation,
  useCreateEventDraftFromEmailMutation,
  useCreateEventDraftFromTextMutation,
  useLinkThreadsToEventMutation,
} = eventApi;

// Admin v2 Phase 2 hooks.
export const { useGetAdminPricingQuery, usePatchEventNeedsMutation } = eventApi;

export default eventApi;
export { eventApi };
